import React from 'react';
import { useMediaQuery, useToggle } from 'usehooks-ts';
import { Button } from '@progress/kendo-react-buttons';
import { Reveal } from '@progress/kendo-react-animation';
import { Loader } from '@progress/kendo-react-indicators';
import { ComboBox } from '@progress/kendo-react-dropdowns';
import { ExpansionPanel } from '@progress/kendo-react-layout';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

import Toolbar from './toolbar';
import { LaunchGameAsync } from '../../services/games';
import { useBreadcrumb } from '../../contexts/breadcrumb';
import { GameDocumentContext } from '../../contexts/game-document';
import { useGetEnvironments } from '../../hooks/services/use-environments';
import { ExpansionContentDisabledKeys } from '../../components/expansion-content';
import { QRTaskPanel } from '../../components/activity-event/qr-task';

const AssetCard: React.FC<{
  icon: string;
  boxColor: string;
  text: string;
  count: number;
}> = ({ boxColor, count, icon, text }) => {
  return (
    <div
      style={{
        display: 'flex',
        gap: '21px',
        boxShadow: '0px 4px 4px 0px #00000014',
        padding: '13px 25px',
        backgroundColor: '#F8FCFE',
        borderRadius: '4px'
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '5px',
          padding: '8px 4px',
          backgroundColor: boxColor,
          height: '50px',
          width: '50px'
        }}>
        <span className={'material-symbols-outlined text-white'}>{icon}</span>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
        <span
          style={{
            fontSize: '16px',
            lineHeight: '16px'
          }}>
          {text}
        </span>
        <span
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            lineHeight: '28px'
          }}>
          {count}
        </span>
      </div>
    </div>
  );
};

const EnvironmentDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { gameId } = useParams();
  const { data, isLoading } = useGetEnvironments();

  const [selectedEnv, setSelectedEnv] = React.useState('');

  const onLaunchGame = () => {
    if (!gameId || !selectedEnv) return;

    LaunchGameAsync(parseInt(gameId), parseInt(selectedEnv))
      .then((result: string) => {
        const gameUrl = result.replaceAll('"', '');
        window.open(`${gameUrl}`);
        window.open(`${gameUrl.replace('/games/', '/preload/')}`);
        window.open(`${gameUrl}/facilitator`);
      })
      .finally(() => {
        onClose();
      });
  };

  return (
    <Dialog
      modal={true}
      onClose={onClose}
      width={400}
      title={'Select environment'}>
      <p>This will launch the latest game document</p>
      <ComboBox
        loading={isLoading}
        disabled={isLoading}
        data={data?.data ?? []}
        dataItemKey={'id'}
        textField={'name'}
        filterable={true}
        onChange={(e) => setSelectedEnv(e.value ? e.value.id : '')}
      />
      <DialogActionsBar>
        <Button themeColor={'secondary'} onClick={onClose}>
          Cancel
        </Button>
        <Button
          themeColor={'primary'}
          disabled={!selectedEnv}
          onClick={onLaunchGame}>
          Launch
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

const SavePromptDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <Dialog modal={true} onClose={onClose} width={400} title={'Save you game'}>
      <p>Please save your game and then launch a test.</p>
      <DialogActionsBar>
        <Button themeColor={'secondary'} onClick={onClose}>
          Close
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export const GameTestLab: React.FC = () => {
  const { gameId } = useParams();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1199px)');

  const column = React.useMemo(() => {
    if (isMobile) return '1';
    if (isTablet) return '2';

    return '3';
  }, [isMobile, isTablet]);

  const [state] = React.useContext(GameDocumentContext);
  const [showJSONPanel, toggleShowJSONPanel] = useToggle();
  const [showEnvDialog, toggleEnvDialog] = useToggle();
  const [showSaveDialog, toggleSaveDialog] = useToggle();

  const { data, isLoading } = useGetEnvironments();

  const onLaunchGame = () => {
    if (state.isDirty) {
      toggleSaveDialog();
      return;
    }

    if ((data?.data ?? []).length > 1) {
      toggleEnvDialog();
      return;
    }

    const env = data?.data?.[0].id;
    if (!gameId || !env) return;

    LaunchGameAsync(parseInt(gameId), env).then((result: string) => {
      const gameUrl = result.replaceAll('"', '');
      window.open(`${gameUrl}`);
      window.open(`${gameUrl.replace('/games/', '/preload/')}`);
      window.open(`${gameUrl}/facilitator`);
    });
  };

  React.useEffect(() => {
    // Set the page title.
    document.title = `Test Lab - ${state.gameDocument?.name}`;
  }, [state]);

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  React.useEffect(() => {
    let match = matchPath('/designer/:gameId/test-lab/*', location.pathname);
    if (match) setBreadcrumb([{ text: 'Test Lab', to: match.pathnameBase }]);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar
        title={'Test Lab'}
        buttonHelpSupport={{
          title: 'Game testing guide',
          url: 'https://forum.catalystglobal.com/t/4562'
        }}
      />
      <div
        className={'d-flex flex-column'}
        style={{
          marginTop: '16px',
          gap: '16px'
        }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '17px',
            padding: '27px',
            backgroundColor: '#F8FCFE',
            border: '1px solid #CFD4D9',
            width: '100%'
          }}>
          <span style={{ fontWeight: 'bold' }}>Test your game</span>
          <span>
            Launch a test version of your game to validate the player and
            facilitator experience before publishing.
          </span>
          <Button
            style={{
              width: '255px'
            }}
            themeColor={'error'}
            disabled={isLoading}
            onClick={onLaunchGame}>
            {isLoading ? (
              <Loader themeColor={'light'} className={'mr-2'} />
            ) : (
              <span className={'material-symbols-outlined text-white mr-2'}>
                rocket_launch
              </span>
            )}
            Launch Test
          </Button>
          <span>Test sessions remain active for 1 hour.</span>
        </div>

        <div>
          <span>Your game at a glance</span>
          <hr />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${column}, minmax(0, 1fr))`,
              columnGap: '16px',
              rowGap: '16px'
            }}>
            {[
              {
                boxColor: '#4698CB',
                count: state.gameDocument?.assets.tasks?.length ?? 0,
                icon: 'checklist',
                text: 'Tasks'
              },
              {
                boxColor: '#14BF8B',
                count: state.gameDocument?.assets.zones?.length ?? 0,
                icon: 'activity_zone',
                text: 'Zone Maps'
              },
              {
                boxColor: '#FEB333',
                count: state.gameDocument?.assets.areas?.length ?? 0,
                icon: 'border_inner',
                text: 'Areas'
              },
              {
                boxColor: '#003A64',
                count: state.gameDocument?.assets.titles?.length ?? 0,
                icon: 'sentiment_satisfied',
                text: 'Titles'
              },
              {
                boxColor: '#D42733',
                count: state.gameDocument?.assets.items?.length ?? 0,
                icon: 'wand_stars',
                text: 'Inventory Items'
              },
              {
                boxColor: '#6262AB',
                count: state.gameDocument?.assets.timers?.length ?? 0,
                icon: 'avg_pace',
                text: 'Timers'
              }
            ].map((asset) => (
              <AssetCard {...asset} />
            ))}
          </div>
        </div>

        <QRTaskPanel gameDocument={state.gameDocument!} />

        <ExpansionPanel
          title={
            <div className={'d-flex flex-column'}>
              <span className={'fw-bold'}>Diagnostics</span>
              <small className={'text-muted'} style={{ fontWeight: 'normal' }}>
                Raw game data for developers (JSON)
              </small>
            </div>
          }
          subtitle={showJSONPanel ? 'Hide' : 'Show'}
          expanded={showJSONPanel}
          onAction={toggleShowJSONPanel}>
          <Reveal>
            {showJSONPanel && (
              <ExpansionContentDisabledKeys>
                <div className={'d-flex flex-column'}>
                  <div className={'d-flex justify-content-end'}>
                    <div
                      className={
                        'd-flex align-items-center cursor-pointer text-secondary text-decoration-none'
                      }
                      onClick={() => {
                        navigator.clipboard.writeText(
                          JSON.stringify(state.gameDocument)
                        );
                      }}>
                      <span className={'material-symbols-outlined'}>
                        content_copy
                      </span>
                      <span>Copy JSON</span>
                    </div>
                  </div>
                  <pre style={{ fontSize: '0.7rem' }}>
                    {JSON.stringify(state.gameDocument, null, 2)}
                  </pre>
                </div>
              </ExpansionContentDisabledKeys>
            )}
          </Reveal>
        </ExpansionPanel>
      </div>

      {showEnvDialog && <EnvironmentDialog onClose={toggleEnvDialog} />}
      {showSaveDialog && <SavePromptDialog onClose={toggleSaveDialog} />}
    </>
  );
};
